import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaUser } from "react-icons/fa";
import { PiCertificateFill } from "react-icons/pi";
import { useApproveStatusMutation } from "../../Redux/app/appApis.js";
import { useGetAllUsersQuery } from "../../Redux/auth/authApis.js";
import Navbar from "../Navbar/Navbar";
import { toast } from "react-toastify";
import { DocumentModal, FullImageModal, UserCard } from "./ui/dashBoardUis.js";

const AdminDashboard = () => {
  const [showSection, setShowSection] = useState("approvals");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [fullImage, setFullImage] = useState(null);
  const navigate = useNavigate();

  const { data: users = [], isLoading, error } = useGetAllUsersQuery();
  const [approveStatus, { isLoading: isApproving }] =
    useApproveStatusMutation();

  const openModal = (documentList) => {
    setDocuments(documentList);
    setIsModalOpen(true);
  };

  const handleApprove = async (userId) => {
    try {
      await approveStatus(userId).unwrap();
      toast.success(`User approved successfully`);
    } catch (error) {
      toast.error("Failed to approve user");
    }
  };

  const handleCertificationClick = () => {
    navigate("/certificate");
  };

  const totalUsers = users.length;
  const approvedUsers = users.filter((u) => u.status === "Approved").length;
  const pendingUsers = totalUsers - approvedUsers;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <Navbar />
      <div className="flex flex-col md:flex-row flex-1">
        {/* Sidebar */}
        <div className="w-full md:w-[20%] bg-[#D80000] text-white p-6">
          <h2 className="text-2xl font-bold mb-6">Admin Dashboard</h2>
          <ul>
            <li
              className={`cursor-pointer py-3 px-4 font-semibold rounded-lg flex items-center gap-3 transition-all ${
                showSection === "approvals"
                  ? "bg-white text-[#D80000] shadow-lg"
                  : "hover:bg-white/10"
              }`}
              onClick={() => setShowSection("approvals")}
            >
              <FaCheckCircle size={20} />
              <span>User Approvals</span>
            </li>
            <li
              className={`cursor-pointer py-3 px-4 font-semibold rounded-lg flex items-center gap-3 transition-all ${
                showSection === "certifications"
                  ? "bg-white text-[#D80000] shadow-lg"
                  : "hover:bg-white/10"
              }`}
              onClick={handleCertificationClick}
            >
              <PiCertificateFill size={20} />
              <span>Certifications</span>
            </li>
          </ul>
        </div>

        <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
          {showSection === "approvals" && (
            <div>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  User Approvals
                </h1>
                <p className="text-gray-600">
                  Review and approve user registrations
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Total Users
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {totalUsers}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <FaUser className="text-blue-600 text-xl" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Approved
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {approvedUsers}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <FaCheckCircle className="text-green-600 text-xl" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Pending
                      </p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {pendingUsers}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-yellow-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading && (
                <div className="flex justify-center items-center py-20">
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 border-[#D80000] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">
                      Loading users...
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-6 h-6 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-red-800 font-medium">
                      Error loading users: {error.message}
                    </p>
                  </div>
                </div>
              )}

              {!isLoading && !error && (
                <div>
                  {users.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FaUser className="text-gray-400 text-3xl" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        No Users Found
                      </h3>
                      <p className="text-gray-600">
                        There are no users to display at the moment.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                      {users.map((user) => (
                        <UserCard
                          key={user._id}
                          user={user}
                          onApprove={handleApprove}
                          onViewDocuments={openModal}
                          isApproving={isApproving}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <DocumentModal
          documents={documents}
          onClose={() => setIsModalOpen(false)}
          onImageClick={(image) => setFullImage(image)}
        />
      )}

      {fullImage && (
        <FullImageModal image={fullImage} onClose={() => setFullImage(null)} />
      )}
    </div>
  );
};

<UserCard />;
<DocumentModal />;
<FullImageModal />;
export default AdminDashboard;
